import { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Smartphone, Monitor, Apple, Share, MoreVertical, Plus, Download, Menu, HelpCircle, ChevronDown, ChevronUp, AlertTriangle, CheckCircle2, X } from "lucide-react";
import { useTranslation } from "react-i18next";

type DeviceTab = "android" | "ios" | "desktop";

/* ── Device & browser detection ── */
const detectDevice = (): DeviceTab => {
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return "ios";
  if (/Android/.test(ua)) return "android";
  return "desktop";
};

const isSamsungBrowser = () => /SamsungBrowser/i.test(navigator.userAgent);
const isIpad = () => /iPad/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
export const isIosSafari = () => {
  const ua = navigator.userAgent;
  return /iPhone|iPad|iPod/.test(ua) && /Safari/.test(ua) && !/Chrome|CriOS|FxiOS|OPiOS/.test(ua);
};
const isIosChrome = () => /CriOS/.test(navigator.userAgent);
const isIosFirefox = () => /FxiOS/.test(navigator.userAgent);
const getIosMajorVersion = (): number | null => {
  const match = navigator.userAgent.match(/OS (\d+)_/);
  return match ? parseInt(match[1], 10) : null;
};
/** iOS 26 moved the Share button behind a "•••" menu next to the address bar. */
const isIos26Plus = () => {
  const v = getIosMajorVersion();
  return v !== null && v >= 26;
};
export const isStandalone = () =>
  (window.navigator as any).standalone === true ||
  window.matchMedia("(display-mode: standalone)").matches;

interface InstallGuideModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onNativeInstall?: () => void;
  iosOnly?: boolean;
}

const InstallGuideModal = ({ open, onOpenChange, onNativeInstall, iosOnly }: InstallGuideModalProps) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<DeviceTab>(iosOnly ? "ios" : detectDevice());
  const [showHelp, setShowHelp] = useState(false);
  const [alreadyInstalled, setAlreadyInstalled] = useState(false);

  useEffect(() => {
    if (!open) return;
    const device = iosOnly ? "ios" : detectDevice();
    setActiveTab(device);
    setShowHelp(false);
    setAlreadyInstalled(isStandalone());
  }, [open, iosOnly]);

  const handleTabChange = useCallback((tab: DeviceTab) => {
    setActiveTab(tab);
    setShowHelp(false);
  }, []);

  const handleHelpToggle = () => {
    const next = !showHelp;
    setShowHelp(next);
  };

  const handleClose = (v: boolean) => {
    onOpenChange(v);
  };

  const tabs: { key: DeviceTab; label: string; icon: React.ReactNode }[] = [
    { key: "android", label: "Android", icon: <Smartphone size={16} /> },
    { key: "ios",     label: "iPhone",  icon: <Apple size={16} /> },
    { key: "desktop", label: t("install_guide.computer"), icon: <Monitor size={16} /> },
  ];
  const visibleTabs = iosOnly ? tabs.filter(t => t.key === "ios") : tabs;

  /* ── iOS: wrong browser warning ── */
  const showIosWrongBrowser = activeTab === "ios" && (isIosChrome() || isIosFirefox());
  const wrongBrowserName = isIosChrome() ? "Chrome" : isIosFirefox() ? "Firefox" : "este navegador";

  /* ── Steps per device ── */
  const androidSteps = isSamsungBrowser()
    ? [
        { num: 1, emoji: "☰", text: t("install_guide.android_samsung_1") },
        { num: 2, emoji: "➕", text: t("install_guide.android_samsung_2") },
        { num: 3, emoji: "✅", text: t("install_guide.android_samsung_3") },
      ]
    : [
        { num: 1, emoji: "⋮", text: t("install_guide.android_chrome_1") },
        { num: 2, emoji: "📲", text: t("install_guide.android_chrome_2") },
        { num: 3, emoji: "✅", text: t("install_guide.android_chrome_3") },
      ];

  const iosDevice = isIpad() ? t("install_guide.ipad") : "iPhone";
  const isIos26 = isIos26Plus();

  // iOS 26 moved Share behind a "•••" menu next to the address bar (See More → Add to Home Screen).
  const iosStepsModern = [
    { num: 1, emoji: "•••", text: t("install_guide.ios26_step1"), hint: t("install_guide.ios26_step1_hint") },
    { num: 2, emoji: "📤", text: t("install_guide.ios26_step2"), hint: null },
    { num: 3, emoji: "📜", text: t("install_guide.ios26_step3"), hint: null },
    { num: 4, emoji: "➕", text: t("install_guide.ios26_step4"), hint: null },
    { num: 5, emoji: "✅", text: t("install_guide.ios_step4", { device: iosDevice }), hint: null },
  ];

  // Classic flow: Share button (□↑) at the bottom of Safari → Add to Home Screen.
  const iosStepsClassic = [
    {
      num: 1,
      emoji: "⬆️",
      text: t("install_guide.ios_step1", { device: iosDevice }),
      hint: t("install_guide.ios_step1_hint"),
    },
    {
      num: 2,
      emoji: "📜",
      text: t("install_guide.ios_step2"),
      hint: t("install_guide.ios_step2_hint"),
    },
    {
      num: 3,
      emoji: "➕",
      text: t("install_guide.ios_step3"),
      hint: null,
    },
    {
      num: 4,
      emoji: "✅",
      text: t("install_guide.ios_step4", { device: iosDevice }),
      hint: null,
    },
  ];

  const iosSteps = isIos26 ? iosStepsModern : iosStepsClassic;

  const desktopSteps = [
    { num: 1, emoji: "🌐", text: t("install_guide.desktop_1") },
    { num: 2, emoji: "⊕",  text: t("install_guide.desktop_2") },
    { num: 3, emoji: "⋮",  text: t("install_guide.desktop_3") },
    { num: 4, emoji: "✅", text: t("install_guide.desktop_4") },
  ];

  /* ── iOS help tips ── */
  const iosHelpTips = [
    { emoji: "🔍", text: t("install_guide.ios_help_1") },
    { emoji: "🕵️", text: t("install_guide.ios_help_2") },
    { emoji: "🔄", text: t("install_guide.ios_help_3") },
    { emoji: "📍", text: t("install_guide.ios_help_4") },
  ];

  /* ── Android help tips ── */
  const androidHelpTips = [
    { emoji: "🌐", text: t("install_guide.android_help_1") },
    { emoji: "🔄", text: t("install_guide.android_help_2") },
    { emoji: "📍", text: t("install_guide.android_help_3") },
  ];

  const helpTips = activeTab === "ios" ? iosHelpTips : androidHelpTips;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-sm rounded-2xl p-0 overflow-hidden border-border/60 bg-card">

        {/* Header */}
        <DialogHeader className="px-5 pt-5 pb-3">
          <DialogTitle className="text-lg font-extrabold text-center text-foreground">
            {t("install_guide.title")}
          </DialogTitle>
          <p className="text-xs text-muted-foreground text-center mt-0.5">
            {t("install_guide.subtitle_short")}
          </p>
        </DialogHeader>

        {/* Already installed notice */}
        {alreadyInstalled && (
          <div className="mx-5 mb-3 flex items-center gap-2 px-3 py-2 rounded-xl bg-green-50/60 dark:bg-green-950/20 border border-green-200/50">
            <CheckCircle2 size={15} className="text-green-600 shrink-0" />
            <p className="text-xs font-semibold text-green-700 dark:text-green-400">
              {t("install_guide.already_installed")}
            </p>
          </div>
        )}

        {/* Device tabs */}
        {visibleTabs.length > 1 && (
          <div className="flex gap-1 px-5 pb-3">
            {visibleTabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => handleTabChange(tab.key)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-bold transition-all ${
                  activeTab === tab.key
                    ? "bg-primary text-primary-foreground shadow-md"
                    : "bg-muted/50 text-muted-foreground hover:bg-muted"
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>
        )}

        <div className="px-5 pb-5 space-y-4">

          {/* ── iOS: wrong browser warning ── */}
          {showIosWrongBrowser && (
            <div className="flex items-start gap-2.5 p-3 rounded-xl bg-amber-50/70 dark:bg-amber-950/20 border border-amber-200/50">
              <AlertTriangle size={16} className="text-amber-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-bold text-foreground">
                  {t("install_guide.ios_wrong_browser_title", { browser: wrongBrowserName })}
                </p>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  {t("install_guide.ios_wrong_browser_desc")}
                </p>
              </div>
            </div>
          )}

          {/* ── iOS steps ── */}
          {activeTab === "ios" && !showIosWrongBrowser && (
            <div className="space-y-3">
              {iosSteps.map((step) => (
                <div key={step.num} className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm">
                    {step.emoji}
                  </div>
                  <div className="pt-0.5 flex-1">
                    <p className="text-sm font-semibold text-foreground leading-snug">{step.text}</p>
                    {step.hint && (
                      <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">{step.hint}</p>
                    )}
                  </div>
                </div>
              ))}

              {/* Share icon visual hint — differs by iOS version */}
              {isIos26 ? (
                <div className="mt-2 flex items-center gap-2 px-3 py-2.5 rounded-xl bg-muted/40 border border-border/30">
                  <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center shrink-0">
                    <MoreVertical size={16} className="text-foreground rotate-90" />
                  </div>
                  <p className="text-[11px] text-muted-foreground leading-relaxed flex-1">
                    {t("install_guide.ios26_dots_looks_like")}
                  </p>
                </div>
              ) : (
                <div className="mt-2 flex items-center gap-2 px-3 py-2.5 rounded-xl bg-muted/40 border border-border/30">
                  <div className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center shrink-0">
                    <Share size={16} className="text-white" />
                  </div>
                  <p className="text-[11px] text-muted-foreground leading-relaxed flex-1">
                    {t("install_guide.ios_share_looks_like")}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* ── Android steps ── */}
          {activeTab === "android" && (
            <div className="space-y-3">
              {androidSteps.map((step) => (
                <div key={step.num} className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm">
                    {step.emoji}
                  </div>
                  <p className="text-sm font-semibold text-foreground leading-snug pt-1">{step.text}</p>
                </div>
              ))}
            </div>
          )}

          {/* ── Desktop steps ── */}
          {activeTab === "desktop" && (
            <div className="space-y-3">
              {desktopSteps.map((step) => (
                <div key={step.num} className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm">
                    {step.emoji}
                  </div>
                  <p className="text-sm font-semibold text-foreground leading-snug pt-1">{step.text}</p>
                </div>
              ))}
            </div>
          )}

          {/* ── Native install button (Android prompt available) ── */}
          {onNativeInstall && (
            <button
              onClick={() => { onNativeInstall(); handleClose(false); }}
              className="w-full py-3 rounded-xl bg-primary text-primary-foreground font-bold text-sm transition-all hover:opacity-90 active:scale-[0.98] flex items-center justify-center gap-2"
            >
              <Download size={16} />
              {t("install_guide.install_now")}
            </button>
          )}

          {/* ── "Still not seeing it?" toggle ── */}
          {(activeTab === "ios" || activeTab === "android") && (
            <button
              onClick={handleHelpToggle}
              className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl border border-border/40 text-xs font-semibold text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-all"
            >
              <HelpCircle size={14} />
              {t("install_guide.still_not_seeing")}
              {showHelp ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            </button>
          )}

          {/* ── Help tips (expanded) ── */}
          {showHelp && (
            <div className="rounded-xl bg-muted/30 border border-border/30 p-3.5 space-y-2.5">
              <p className="text-[11px] font-bold text-foreground uppercase tracking-wide">
                {t("install_guide.help_check_these")}
              </p>
              {helpTips.map((tip, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className="text-sm shrink-0">{tip.emoji}</span>
                  <p className="text-xs text-muted-foreground leading-relaxed">{tip.text}</p>
                </div>
              ))}
              <div className="pt-1 border-t border-border/30">
                <p className="text-[11px] text-muted-foreground">
                  {t("install_guide.help_still_stuck")}
                  {" "}
                  <a href="/support" className="text-primary font-semibold underline underline-offset-2" onClick={() => handleClose(false)}>
                    {t("install_guide.help_contact")}
                  </a>
                </p>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InstallGuideModal;
